import { Card, CardContent, Typography, Box, Stack } from '@mui/material'
import {
  TrendingUp,
  Receipt,
  Business,
  AttachMoney,
  AccountBalance,
  ReceiptLong,
  AccountBalanceWallet,
  HourglassEmpty,
  WarningAmber,
  CheckCircle,
  PendingActions,
  Schedule,
} from '@mui/icons-material'
import { KPISummary } from '@/types'
import { formatCurrency, formatCompactNumber } from '@/utils/formatters'

interface KPICardsProps {
  kpi: KPISummary
  isLoading?: boolean
  showTax?: boolean
}

interface KPICardProps {
  title: string
  value: string
  subtitle: string
  icon: React.ReactNode
  color: 'primary' | 'secondary' | 'success' | 'info' | 'warning'
}

function KPICard({ title, value, subtitle, icon, color }: KPICardProps) {
  const colorMap = {
    primary: 'primary.main',
    secondary: 'secondary.main',
    success: 'success.main',
    info: 'info.main',
    warning: 'warning.main',
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: `${colorMap[color]}.main`,
              opacity: 0.15,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: `${colorMap[color]}.main`,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h4" fontWeight="600" color="textPrimary">
            {value}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" fontWeight="500">
          {title}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  )
}

export default function KPICards({ kpi, isLoading, showTax = false }: KPICardsProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Box
            key={i}
            sx={{
              flex: { xs: '1 1 200px', md: '1 1 0' },
              height: 120,
              bgcolor: 'action.hover',
              borderRadius: 2,
            }}
          />
        ))}
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      {/* Status / approval KPIs */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <KPICard
          title="Outstanding"
          value={formatCompactNumber(kpi.outstandingAmount)}
          subtitle={`${kpi.outstandingCount} unpaid · ${formatCurrency(kpi.outstandingAmount)}`}
          icon={<HourglassEmpty sx={{ fontSize: 28 }} />}
          color="primary"
        />
        <KPICard
          title="Overdue"
          value={formatCompactNumber(kpi.overdueAmount)}
          subtitle={`${kpi.overdueCount} overdue · ${formatCurrency(kpi.overdueAmount)}`}
          icon={<WarningAmber sx={{ fontSize: 28 }} />}
          color="warning"
        />
        <KPICard
          title="Due soon (7d)"
          value={formatCompactNumber(kpi.dueSoonAmount)}
          subtitle={`${kpi.dueSoonCount} due soon · ${formatCurrency(kpi.dueSoonAmount)}`}
          icon={<Schedule sx={{ fontSize: 28 }} />}
          color="secondary"
        />
        <KPICard
          title="Approved"
          value={String(kpi.approvedCount)}
          subtitle="Approved for payment"
          icon={<CheckCircle sx={{ fontSize: 28 }} />}
          color="success"
        />
        <KPICard
          title="Pending"
          value={String(kpi.pendingCount)}
          subtitle="Open + in review"
          icon={<PendingActions sx={{ fontSize: 28 }} />}
          color="info"
        />
      </Box>

      {/* Core financial KPIs */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <KPICard
          title="Total Net"
          value={formatCompactNumber(kpi.totalNetto)}
          subtitle={formatCurrency(kpi.totalNetto)}
          icon={<AttachMoney sx={{ fontSize: 28 }} />}
          color="primary"
        />
        <KPICard
          title="Total Gross"
          value={formatCompactNumber(kpi.totalBrutto)}
          subtitle={formatCurrency(kpi.totalBrutto)}
          icon={<TrendingUp sx={{ fontSize: 28 }} />}
          color="secondary"
        />
        <KPICard
          title="Invoices"
          value={String(kpi.invoiceCount)}
          subtitle="Total records"
          icon={<Receipt sx={{ fontSize: 28 }} />}
          color="info"
        />
        <KPICard
          title="Suppliers"
          value={String(kpi.supplierCount)}
          subtitle="Unique suppliers"
          icon={<Business sx={{ fontSize: 28 }} />}
          color="success"
        />
      </Box>

      {/* Tax detail KPIs (toggleable) */}
      {showTax && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <KPICard
            title="VAT Amount"
            value={formatCompactNumber(kpi.totalVatAmount)}
            subtitle={formatCurrency(kpi.totalVatAmount)}
            icon={<AccountBalance sx={{ fontSize: 28 }} />}
            color="info"
          />
          <KPICard
            title="Tax-Free Amount"
            value={formatCompactNumber(kpi.totalTaxFreeAmount)}
            subtitle={formatCurrency(kpi.totalTaxFreeAmount)}
            icon={<ReceiptLong sx={{ fontSize: 28 }} />}
            color="warning"
          />
          <KPICard
            title="Taxable Net"
            value={formatCompactNumber(kpi.totalTaxableNetAmount)}
            subtitle={formatCurrency(kpi.totalTaxableNetAmount)}
            icon={<AccountBalanceWallet sx={{ fontSize: 28 }} />}
            color="secondary"
          />
        </Box>
      )}
    </Stack>
  )
}
